import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {InboxesFiltersEnum, TeammatesFoldersEnum} from '../../../src/models/url/models/inboxesUrlModel';
import {makeGetHasAssigneeLensCriteria} from '../../../src/store/data/lenses/lensesSelectors';
import {getShouldUseOpenFilterForTeams} from '../../../src/store/data/preferences/preferencesSelectors';
import {getStarredTagIds} from '../../../src/store/data/tags/tagsSelectors';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../../src/store/panels/inboxes/parameters/inboxesParameters';
import {AppParametricSelector, createSelector} from '../../helpers/reselectHelpers';

/*
 * Constants.
 */

const teammateFilters = Object.freeze([
  InboxesFiltersEnum.OPEN,
  InboxesFiltersEnum.ARCHIVED,
  InboxesFiltersEnum.SNOOZED,
  InboxesFiltersEnum.TRASHED,
  InboxesFiltersEnum.SPAM,
]);

const teamFilters = Object.freeze([
  InboxesFiltersEnum.UNASSIGNED,
  InboxesFiltersEnum.ASSIGNED,
  InboxesFiltersEnum.ARCHIVED,
  InboxesFiltersEnum.SNOOZED,
  InboxesFiltersEnum.TRASHED,
  InboxesFiltersEnum.SPAM,
]);

const globalFilters = Object.freeze([
  InboxesFiltersEnum.OPEN,
  InboxesFiltersEnum.ARCHIVED,
  InboxesFiltersEnum.SNOOZED,
]);

/*
 * Selectors.
 */

function selectTeammateFiltersForFolder(folder: TeammatesFoldersEnum) {
  if (folder === TeammatesFoldersEnum.DISCUSSIONS) {
    return teammateFilters.filter((filter) => filter !== InboxesFiltersEnum.SPAM);
  }

  return teammateFilters;
}

export function makeGetCellsFilters<P>(getParameters: AppParametricSelector<P, InboxesParameters>) {
  const getHasAssigneeLensCriteria = makeGetHasAssigneeLensCriteria(getParameters);
  return createSelector(
    [getStarredTagIds, getShouldUseOpenFilterForTeams, getHasAssigneeLensCriteria, getParameters],
    computeCellsFilters,
  );
}

export function makeGetHasCellsFilters<P>(
  getParameters: AppParametricSelector<P, InboxesParameters>,
): AppParametricSelector<P, boolean> {
  const getCellsFilters = makeGetCellsFilters(getParameters);

  return (state, props) => getCellsFilters(state, props).length > 0;
}

/*
 * Helpers.
 */

export function computeCellsFilters(
  starredTagIds: ReadonlySet<number>,
  shouldUseOpenFilterForTeams: boolean,
  shouldUseOpenFilterForLens: boolean,
  parameters: InboxesParameters,
): readonly InboxesFiltersEnum[] {
  // If this is a starred tag, we hide all filters.
  if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_VIEW && starredTagIds.has(parameters.viewId)) {
    return [];
  }

  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAM:
      return shouldUseOpenFilterForTeams ? teammateFilters : teamFilters;

    case InboxesParametersTypesEnum.TEAM_LENS:
    case InboxesParametersTypesEnum.TEAMMATE_LENS: // Teammate lenses consist of team inboxes.
      return shouldUseOpenFilterForLens || shouldUseOpenFilterForTeams ? teammateFilters : teamFilters;

    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
    case InboxesParametersTypesEnum.VIEW:
      return teammateFilters;

    case InboxesParametersTypesEnum.USER:
      return selectTeammateFiltersForFolder(parameters.folder);

    case InboxesParametersTypesEnum.GLOBAL:
    case InboxesParametersTypesEnum.TEAMMATE:
      return globalFilters;

    case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
      return [];

    default:
      throw new UnreachableError(parameters);
  }
}
